import React, { useState, useEffect, useCallback } from 'react';
import { Download, FileText, CheckCircle, XCircle, Clock, Filter, RefreshCw, AlertCircle, Loader, CreditCard } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n/hooks/useTranslation';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import { formatDate as formatDateUtil } from '../utils/dateUtils';

interface EscrowPaymentRelease {
  _id: string;
  escrowPaymentId: {
    _id: string;
    projectId?: any;
    quoteId?: any;
    amount: number;
    currency: string;
    paymentMethod: 'STRIPE' | 'BANK_TRANSFER';
  };
  accountId: string;
  amount: number;
  currency: string;
  status: 'REQUESTED' | 'COMPLETED' | 'ERROR';
  referenceNumber: string;
  bankTransactionId?: string;
  processedAt?: string;
  processedBy?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  errorMessage?: string;
  createdAt: string;
}

interface EscrowPayment {
  _id: string;
  projectId?: any;
  quoteId?: any;
  accountId: string;
  amount: number;
  currency: string;
  paymentMethod: 'STRIPE' | 'BANK_TRANSFER';
  status: 'PENDING' | 'COMPLETED' | 'RELEASED' | 'CLAIMED' | 'REFUNDED';
  createdAt: string;
  releasedAt?: string;
  claimedAt?: string;
}

export const Escrow: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { setCustomBreadcrumbs } = useBreadcrumbs();
  const [activeTab, setActiveTab] = useState<'releases' | 'transactions'>('releases');
  const [releases, setReleases] = useState<EscrowPaymentRelease[]>([]);
  const [payments, setPayments] = useState<EscrowPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'REQUESTED' | 'COMPLETED' | 'ERROR'>('ALL');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<'ALL' | 'PENDING' | 'COMPLETED' | 'RELEASED' | 'CLAIMED' | 'REFUNDED'>('ALL');
  const [error, setError] = useState<string | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    setCustomBreadcrumbs([
      { label: t('navigation.home'), path: '/', icon: 'Home' },
      { label: t('navigation.escrow'), path: '/escrow', icon: 'DollarSign' }
    ]);
    return () => setCustomBreadcrumbs(null);
  }, [t, setCustomBreadcrumbs]);

  const fetchReleases = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (statusFilter !== 'ALL') {
        params.append('status', statusFilter);
      }
      // System admin can see all releases
      if (user?.role === 'System Administrator') {
        params.append('all', 'true');
      }

      const response = await fetch(`${apiUrl}/api/escrow-payment-releases?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setReleases(data.data || []);
      }
    } catch (error) {
      console.error('Fetch releases error:', error);
      setError('Failed to load payment releases');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, user?.role, apiUrl]);

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (paymentStatusFilter !== 'ALL') {
        params.append('status', paymentStatusFilter);
      }
      // System admin can see all payments
      if (user?.role === 'System Administrator') {
        params.append('all', 'true');
      }

      const response = await fetch(`${apiUrl}/api/escrow-payments/admin/all?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setPayments(data.data || []);
      }
    } catch (error) {
      console.error('Fetch payments error:', error);
      setError('Failed to load escrow payments');
    } finally {
      setLoading(false);
    }
  }, [paymentStatusFilter, user?.role, apiUrl]);

  useEffect(() => {
    if (activeTab === 'releases') {
      fetchReleases();
    } else {
      fetchPayments();
    }
  }, [activeTab, statusFilter, paymentStatusFilter, fetchReleases, fetchPayments]);

  const handleDownloadOFX = async () => {
    try {
      setDownloading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('status', 'REQUESTED'); // Only download REQUESTED releases
      // System admin can download all releases
      if (user?.role === 'System Administrator') {
        params.append('all', 'true');
      }

      const response = await fetch(`${apiUrl}/api/escrow-payment-releases/export-ofx?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to download OFX file');
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'escrow-payment-releases.ofx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('OFX download error:', error);
      setError(error instanceof Error ? error.message : 'Failed to download OFX file');
    } finally {
      setDownloading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount / 100);
  };

  const formatDate = (dateString: string) => {
    return formatDateUtil(dateString);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
      case 'CLAIMED':
        return <CheckCircle className="w-5 h-5 text-success-600" />;
      case 'ERROR':
      case 'REFUNDED':
        return <XCircle className="w-5 h-5 text-error-600" />;
      case 'RELEASED':
        return <CheckCircle className="w-5 h-5 text-accent-600" />;
      default:
        return <Clock className="w-5 h-5 text-warning-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
      case 'CLAIMED':
        return 'bg-success-100 text-success-800 dark:bg-gray-700 dark:border dark:border-success-800/50 dark:text-success-200';
      case 'ERROR':
      case 'REFUNDED':
        return 'bg-error-100 text-error-800 dark:bg-gray-700 dark:border dark:border-error-800/50 dark:text-error-200';
      case 'RELEASED':
        return 'bg-accent-100 text-accent-800 dark:bg-gray-700 dark:border dark:border-accent-800/50 dark:text-accent-200';
      default:
        return 'bg-warning-100 text-warning-800 dark:bg-gray-700 dark:border dark:border-warning-800/50 dark:text-warning-200';
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          {t('navigation.escrow') || 'Escrow'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t('escrow.description') || 'View payment releases and transactions for 3pM Escrow'}
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('releases')}
              className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'releases'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <FileText className="w-5 h-5" />
              <span>{t('escrow.paymentReleases') || 'Payment Releases'}</span>
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'transactions'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <CreditCard className="w-5 h-5" />
              <span>{t('escrow.transactions') || 'Transactions'}</span>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'releases' ? (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('escrow.paymentReleases') || 'Payment Releases'}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {t('escrow.paymentReleasesDescription') || 'View payment releases and download OFX file for bank upload'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={fetchReleases}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    {t('common.refresh') || 'Refresh'}
                  </button>
                  <button
                    onClick={handleDownloadOFX}
                    disabled={downloading}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    {downloading ? (t('common.downloading') || 'Downloading...') : (t('escrow.downloadOFX') || 'Download OFX File')}
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border border-red-200 dark:border-red-800 rounded-lg text-error-800 dark:text-error-200">
                  <AlertCircle className="w-5 h-5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Filters */}
              <div className="flex items-center gap-3">
                <Filter className="w-5 h-5 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="ALL">{t('payment.allStatuses') || 'All Statuses'}</option>
                  <option value="REQUESTED">{t('payment.requested') || 'Requested'}</option>
                  <option value="COMPLETED">{t('payment.completed') || 'Completed'}</option>
                  <option value="ERROR">{t('payment.error') || 'Error'}</option>
                </select>
              </div>

              {/* Releases Table */}
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="w-8 h-8 text-primary-600 animate-spin" />
                </div>
              ) : releases.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  {t('payment.noReleasesFound') || 'No payment releases found'}
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('payment.referenceNumber') || 'Reference'}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('payment.amount') || 'Amount'}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('payment.status') || 'Status'}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('payment.createdAt') || 'Created'}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('payment.processedAt') || 'Processed'}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('payment.bankTransactionId') || 'Bank Transaction ID'}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {releases.map((release) => (
                          <tr key={release._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="font-mono text-sm text-gray-900 dark:text-gray-100">
                                {release.referenceNumber}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {formatCurrency(release.amount, release.currency)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(release.status)}`}>
                                {getStatusIcon(release.status)}
                                {release.status}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {formatDate(release.createdAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {release.processedAt ? formatDate(release.processedAt) : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="font-mono text-sm text-gray-900 dark:text-gray-100">
                                {release.bankTransactionId || '-'}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('escrow.transactions') || 'Escrow Transactions'}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {t('escrow.transactionsDescription') || 'View all escrow payment transactions'}
                  </p>
                </div>
                <button
                  onClick={fetchPayments}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  {t('common.refresh') || 'Refresh'}
                </button>
              </div>

              {/* Filters */}
              <div className="flex items-center gap-3">
                <Filter className="w-5 h-5 text-gray-400" />
                <select
                  value={paymentStatusFilter}
                  onChange={(e) => setPaymentStatusFilter(e.target.value as any)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="ALL">{t('escrow.allStatuses') || 'All Statuses'}</option>
                  <option value="PENDING">{t('escrow.pending') || 'Pending'}</option>
                  <option value="COMPLETED">{t('escrow.completed') || 'Completed'}</option>
                  <option value="RELEASED">{t('escrow.released') || 'Released'}</option>
                  <option value="CLAIMED">{t('escrow.claimed') || 'Claimed'}</option>
                  <option value="REFUNDED">{t('escrow.refunded') || 'Refunded'}</option>
                </select>
              </div>

              {/* Payments Table */}
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="w-8 h-8 text-primary-600 animate-spin" />
                </div>
              ) : payments.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  {t('escrow.noTransactionsFound') || 'No transactions found'}
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('escrow.paymentId') || 'Payment ID'}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('payment.amount') || 'Amount'}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('escrow.paymentMethod') || 'Payment Method'}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('payment.status') || 'Status'}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('payment.createdAt') || 'Created'}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('escrow.releasedAt') || 'Released'}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {payments.map((payment) => (
                          <tr key={payment._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="font-mono text-sm text-gray-900 dark:text-gray-100">
                                {payment._id.substring(0, 8)}...
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {formatCurrency(payment.amount, payment.currency)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900 dark:text-gray-100">
                                {payment.paymentMethod === 'STRIPE' ? 'Stripe' : 'Bank Transfer'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                                {getStatusIcon(payment.status)}
                                {payment.status}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {formatDate(payment.createdAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {payment.releasedAt ? formatDate(payment.releasedAt) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};




